import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, ButtonBase, Chip, CircularProgress, Link, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HPInput from "@components/common/Input";
import HPButton from "@components/common/Button";
import { useGetDatasetByIdQuery, useLazyGetDatasetQuestionsListQuery } from "@store/api/dataset-api";
import type { DatasetMetric, MetricRepresentedResult } from "@models/Metric";
import type { QuestionDetail } from "@models/Dataset";
import MetricBaseSummaryCell from "@components/features/configuration/metrics/MetricBaseSummaryCell";
import MetricSummaryRow from "@components/features/configuration/metrics/MetricSummaryRow";
import {
  describeMetricSelection,
  formatMetricPopulationLabel,
  getMetricResultTypeLabel,
} from "@utils/metric-source-utils";
import {
  filterAndSortDatasetMetrics,
  getMetricResultCandidates,
} from "@utils/dataset-metric-representation-utils";

export interface ResolvedMetricSelection<TFocusParams> {
  metric: DatasetMetric;
  focusParams: TFocusParams;
  representedResults: MetricRepresentedResult[];
}

interface DatasetMetricSourceSelectorProps<TFocusParams> {
  datasetId: string;
  /** Which metrics are listed at all — Bar Chart: Percentage only; Line Chart: Percentage or Mean. */
  resultTypeFilter: (metric: DatasetMetric) => boolean;
  /** Whether a listed metric is selectable — disabled (with reason) when not yet resolved. */
  isEligible: (metric: DatasetMetric) => { eligible: boolean; reason?: string };
  /** Whether resolving this metric's request params needs the underlying QuestionDetail fetched first. */
  needsQuestionDetailFetch: (metric: DatasetMetric) => boolean;
  /** Chart-specific: metric definition -> this chart's own request/focus params. */
  buildFocusParams: (metric: DatasetMetric, questionDetail: QuestionDetail | null) => TFocusParams;
  /** Shown under "Select Metric*", e.g. "Only metrics with a Percentage result can be used in a Bar Chart." */
  eligibilityCaption: string;
  /** Shown when the dataset has zero eligible metrics (distinct from an empty search). */
  noMetricsMessage: string;
  /** Fires with the resolved selection once a metric is picked and (if
   *  needed) its question details have loaded; null while unresolved. */
  onResolvedChange: (selection: ResolvedMetricSelection<TFocusParams> | null) => void;
}

/**
 * Metric picker + read-only summary for a chart's Metric source path —
 * shared by Bar Chart and Line Chart (PRD-67 explicitly reuses PRD-66's
 * searchable list and selected-definition summary). Chart-specific
 * eligibility/focus-param logic is injected via props; everything else
 * (search, sort, row rendering, "Saved definition" panel) is identical.
 */
function DatasetMetricSourceSelector<TFocusParams>({
  datasetId,
  resultTypeFilter,
  isEligible,
  needsQuestionDetailFetch,
  buildFocusParams,
  eligibilityCaption,
  noMetricsMessage,
  onResolvedChange,
}: DatasetMetricSourceSelectorProps<TFocusParams>) {
  const { data: dataset } = useGetDatasetByIdQuery(datasetId, { skip: !datasetId });
  const metrics = useMemo(
    () => (dataset?.metrics ?? []).filter(resultTypeFilter),
    [dataset?.metrics, resultTypeFilter],
  );

  const [selectedMetric, setSelectedMetric] = useState<DatasetMetric | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [questionDetail, setQuestionDetail] = useState<QuestionDetail | null>(null);
  const [fetchError, setFetchError] = useState(false);

  const [fetchQuestionDetails, { isFetching: isFetchingDetails }] =
    useLazyGetDatasetQuestionsListQuery();

  const runFetch = (metric: DatasetMetric) => {
    if (!metric.source || !datasetId) return;
    setFetchError(false);
    fetchQuestionDetails({
      id: datasetId,
      page: 1,
      page_size: 10,
      search_query: metric.source.question.questionLabel,
      dataset_element_id: metric.source.question.questionId,
      expanded: true,
    })
      .unwrap()
      .then((result) => {
        const detail = result.questions.find(
          (q) => q.datasetElementId === metric.source!.question.questionId,
        );
        setQuestionDetail(detail ?? null);
      })
      .catch(() => setFetchError(true));
  };

  useEffect(() => {
    if (
      !selectedMetric?.source ||
      !datasetId ||
      !needsQuestionDetailFetch(selectedMetric)
    ) {
      setQuestionDetail(null);
      setFetchError(false);
      return;
    }
    runFetch(selectedMetric);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMetric?.id, datasetId]);

  const needsFetch = selectedMetric ? needsQuestionDetailFetch(selectedMetric) : false;
  const isResolving = needsFetch && !questionDetail && !fetchError;

  useEffect(() => {
    if (!selectedMetric || isResolving || fetchError) {
      onResolvedChange(null);
      return;
    }
    onResolvedChange({
      metric: selectedMetric,
      focusParams: buildFocusParams(selectedMetric, questionDetail),
      representedResults: getMetricResultCandidates(selectedMetric, questionDetail),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMetric, questionDetail, isResolving, fetchError]);

  if (selectedMetric) {
    const source = selectedMetric.source;
    const selection = source ? describeMetricSelection(source, questionDetail) : null;
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
          Metric*
        </Typography>
        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
          <Box sx={{ p: 1.5, backgroundColor: "#E3F2FD" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
              >
                {selectedMetric.name}
              </Typography>
              <HPButton
                variant="text"
                size="small"
                onClick={() => setSelectedMetric(null)}
              >
                Change
              </HPButton>
            </Box>
            {source && (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ whiteSpace: "normal", wordBreak: "break-word", display: "block", mt: 0.5 }}
                >
                  {source.question.questionLabel}
                  {" · "}
                  {getMetricResultTypeLabel(source.resultType)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Metric population: {formatMetricPopulationLabel(selectedMetric.population)}
                </Typography>
              </>
            )}
            {isFetchingDetails && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Resolving metric definition...
                </Typography>
              </Box>
            )}
            {fetchError && (
              <Alert
                severity="error"
                sx={{ mt: 1 }}
                action={
                  <HPButton variant="outlined" size="small" onClick={() => runFetch(selectedMetric)}>
                    Retry
                  </HPButton>
                }
              >
                Failed to load this metric's question details.
              </Alert>
            )}
          </Box>

          {source && !isResolving && !fetchError && (
            <Box sx={{ p: 1.5, backgroundColor: "#f7fbfe" }}>
              <Typography variant="caption" fontWeight={700} sx={{ display: "block", mb: 0.75 }}>
                Saved definition
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <MetricSummaryRow label="Source question">
                  <Typography variant="body2" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                    {source.question.questionLabel}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: "normal", wordBreak: "break-word", display: "block" }}
                  >
                    {source.question.questionTitle}
                  </Typography>
                </MetricSummaryRow>
                {selection && (
                  <MetricSummaryRow label="Selection">
                    <Typography variant="body2" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                      {selection.label}
                    </Typography>
                    {selection.details && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: "normal", wordBreak: "break-word", display: "block" }}
                      >
                        {selection.details}
                      </Typography>
                    )}
                  </MetricSummaryRow>
                )}
                <MetricSummaryRow label="Metric population">
                  <Typography variant="body2">
                    {formatMetricPopulationLabel(selectedMetric.population)}
                  </Typography>
                </MetricSummaryRow>
                <MetricSummaryRow label="Base">
                  {datasetId && (
                    <MetricBaseSummaryCell datasetId={datasetId} base={selectedMetric.base} />
                  )}
                </MetricSummaryRow>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Calculation settings are maintained in Dataset Configuration.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  const results = filterAndSortDatasetMetrics(metrics, searchTerm);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" fontWeight={500}>
        Select Metric*
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {eligibilityCaption}
      </Typography>
      <HPInput
        label=""
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search metrics by name or source question..."
      />
      <Box
        sx={{
          mt: 1,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          maxHeight: 280,
          overflow: "auto",
        }}
      >
        {results.length === 0 ? (
          metrics.length === 0 ? (
            <Alert severity="info">
              {noMetricsMessage}{" "}
              {datasetId && (
                <Link
                  href={`/datasets/${datasetId}/config`}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                  Manage Metrics in Dataset Configuration
                  <OpenInNewIcon sx={{ fontSize: 12 }} />
                </Link>
              )}
            </Alert>
          ) : (
            <Alert severity="info">No metrics match your search.</Alert>
          )
        ) : (
          results.map((metric) => {
            const { eligible, reason } = isEligible(metric);
            return (
              <ButtonBase
                key={metric.id}
                disabled={!eligible}
                onClick={() => setSelectedMetric(metric)}
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  px: 1.5,
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:last-child": { borderBottom: "none" },
                  "&:hover": {
                    backgroundColor: "var(--color-hp-background-hover, #F5F5F5)",
                  },
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                >
                  {metric.name}
                </Typography>
                {metric.source && (
                  <>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word", display: "block" }}
                    >
                      {metric.source.question.questionLabel}
                      {" · "}
                      {getMetricResultTypeLabel(metric.source.resultType)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      Metric population: {formatMetricPopulationLabel(metric.population)}
                    </Typography>
                  </>
                )}
                {!eligible && reason && (
                  <Chip
                    label={reason}
                    size="small"
                    sx={{ height: "auto", mt: 0.5, "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 } }}
                  />
                )}
              </ButtonBase>
            );
          })
        )}
      </Box>
    </Box>
  );
}

export default DatasetMetricSourceSelector;
